/**
 * Task attachments, web side: config, upload, and resolving `infras-file:<uuid>` refs to
 * short-lived signed links.
 *
 * Upload = exactly the SCIO portal path: `createUpload` (the api checks kind/type/size and hands
 * back a SIGNED Transforge assembly: service + params + signature + fields) → a headless Uppy v3
 * with @uppy/transloadit pointed at Transforge tus-uploads the bytes into that assembly →
 * `completeUpload(fileId, assemblyId)` (the api tracks the assembly; Transforge writes the
 * processed output). Attaching is gated on `GET <transforgeUrl>/health` from the browser.
 * The markdown keeps only the ref
 * (`![name](infras-file:<uuid>)`); links are minted on read by `fileUrls` and expire
 * (~10–15 min), so `useStoredFile` caches them by id and refetches at 80% of their life.
 * A file still `processing` is polled every 3 s until it is `ready` or `failed`.
 *
 * Contract: docs/files-setup.md.
 */
import { createContext, createElement, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { gql } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { mutationErrorMessage } from './gqlError';
// Uppy is loaded on the first upload (dynamic import below), not with every page that shows a file.
import type Uppy from '@uppy/core';
import type { AssemblyOptions } from '@uppy/transloadit';

import { fileRef } from './fileRefs';
export { FILE_REF_SCHEME, fileIdOf, fileRef, fileRefsToText, markdownUrlTransform } from './fileRefs';

export interface StoredFile {
  id: string;
  kind: string;
  taskId: number | null;
  filename: string;
  contentType: string;
  size: number;
  status: 'uploading' | 'processing' | 'ready' | 'failed' | string;
  processed: boolean;
  url: string | null;
  posterUrl: string | null;
  expiresAt: string | null;
}

export interface FilesConfig {
  uploadsEnabled: boolean;
  /** Transforge gateway the browser uploads to; the health gate probes `<transforgeUrl>/health`. */
  transforgeUrl: string | null;
  imageTypes: string[];
  videoTypes: string[];
  maxImageBytes: number;
  maxVideoBytes: number;
}

export const STORED_FILE_FIELDS = gql`
  fragment StoredFileFields on StoredFile {
    id
    kind
    taskId
    filename
    contentType
    size
    status
    processed
    url
    posterUrl
    expiresAt
  }
`;

export const FILES_CONFIG = gql`
  query FilesConfig {
    filesConfig {
      uploadsEnabled
      transforgeUrl
      imageTypes
      videoTypes
      maxImageBytes
      maxVideoBytes
    }
  }
`;

const CREATE_UPLOAD = gql`
  mutation CreateUpload($input: CreateUploadInput!) {
    createUpload(input: $input) {
      fileId
      ref
      transforge {
        service
        params
        signature
        fields
      }
    }
  }
`;

const COMPLETE_UPLOAD = gql`
  ${STORED_FILE_FIELDS}
  mutation CompleteUpload($fileId: ID!, $assemblyId: String!, $assemblyUrl: String) {
    completeUpload(fileId: $fileId, assemblyId: $assemblyId, assemblyUrl: $assemblyUrl) {
      ...StoredFileFields
    }
  }
`;

const FILE_URLS = gql`
  ${STORED_FILE_FIELDS}
  query FileUrls($ids: [ID!]!) {
    fileUrls(ids: $ids) {
      ...StoredFileFields
    }
  }
`;

/** `filesConfig`, fetched once per session; null while loading or when the api has none. */
export function useFilesConfig(skip = false): FilesConfig | null {
  const { data } = useQuery<{ filesConfig: FilesConfig }>(FILES_CONFIG, { skip, fetchPolicy: 'cache-first' });
  return data?.filesConfig ?? null;
}

/* ── upload ─────────────────────────────────────────────────────────────────── */

export class UploadError extends Error {}

/** A thrown Apollo error → the server's message (policy / magic / rate limit / not configured). */
function serverMessage(e: unknown, fallback: string): string {
  const msg = mutationErrorMessage(e as Parameters<typeof mutationErrorMessage>[0]) || (e as Error)?.message || fallback;
  // a toast reads as a sentence: drop the trailing machine code, e.g. "… (BAD_REQUEST)"
  return msg.replace(/\s*\([A-Z][A-Z0-9_]+\)(?=\s*(?:·|$))/g, '').trim() || fallback;
}

/** What `createUpload` hands back: the file row + a signed Transforge assembly to upload into. */
export interface UploadTicket {
  fileId: string;
  ref: string;
  transforge: {
    /** Transforge gateway base URL (Uppy's `service`). */
    service: string;
    /** Assembly params as a JSON STRING — passed through untouched (the signature covers these exact bytes). */
    params: string;
    /** `sha384:<hex>` over `params`. */
    signature: string;
    /** Assembly fields (form values Transforge echoes back to the notify hook). */
    fields: Record<string, string | number> | string | null;
  };
}

/* ── Transforge health gate (SCIO parity: probe before letting anyone pick a file) ─ */

const TF_HEALTH_TTL_MS = 60_000;
const TF_HEALTH_TIMEOUT_MS = 2500;
const tfHealth = new Map<string, { ok: boolean; at: number }>();
const tfHealthInFlight = new Map<string, Promise<boolean>>();

/**
 * `GET <transforgeUrl>/health` from THIS browser (2.5 s timeout, cached 60 s). Only the browser
 * can tell whether the gateway is reachable from here (CORS / DNS / network); the api never sees
 * that. Fail-closed: timeout, network, CORS or non-2xx = unreachable. No Transloadit fallback.
 */
export async function isTransforgeReachable(endpoint: string): Promise<boolean> {
  const cached = tfHealth.get(endpoint);
  if (cached && Date.now() - cached.at < TF_HEALTH_TTL_MS) return cached.ok;
  const running = tfHealthInFlight.get(endpoint);
  if (running) return running;
  const probe = (async () => {
    let ok = false;
    try {
      // Simple GET, no credentials → no CORS preflight; /health answers ACAO:* .
      const res = await fetch(new URL('/health', endpoint).toString(), {
        method: 'GET',
        signal: AbortSignal.timeout(TF_HEALTH_TIMEOUT_MS),
      });
      ok = res.ok;
    } catch {
      ok = false;
    }
    tfHealth.set(endpoint, { ok, at: Date.now() });
    tfHealthInFlight.delete(endpoint);
    return ok;
  })();
  tfHealthInFlight.set(endpoint, probe);
  return probe;
}

export type UploadServiceState = 'checking' | 'ok' | 'down';
export const UPLOAD_SERVICE_DOWN_HINT = 'Upload service unavailable';

/**
 * The health gate as React state: 'checking' until the first probe lands, then 'ok' / 'down',
 * re-probed when the 60 s cache lapses. `null` endpoint (no config yet / uploads off) = 'down'.
 */
export function useUploadService(endpoint: string | null | undefined): UploadServiceState {
  const [state, setState] = useState<UploadServiceState>(() => {
    if (!endpoint) return 'down';
    const c = tfHealth.get(endpoint);
    return c && Date.now() - c.at < TF_HEALTH_TTL_MS ? (c.ok ? 'ok' : 'down') : 'checking';
  });
  useEffect(() => {
    if (!endpoint) {
      setState('down');
      return;
    }
    let live = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = () => {
      void isTransforgeReachable(endpoint).then((ok) => {
        if (!live) return;
        setState(ok ? 'ok' : 'down');
        timer = setTimeout(run, TF_HEALTH_TTL_MS);
      });
    };
    run();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
  }, [endpoint]);
  return state;
}

/* ── TransforgeUploader: one headless Uppy + Transloadit plugin per file ────────── */

/** Client-side restrictions, from `filesConfig`. The api re-checks the declared type/size at createUpload and the
 *  received size on the pingback; Transforge decodes the bytes, so a non-image/video fails there. */
export interface UploadRestrictions {
  allowedFileTypes?: string[];
  maxFileSize?: number;
}

export function restrictionsFor(cfg: FilesConfig | null | undefined, file: File): UploadRestrictions {
  if (!cfg) return {};
  const allowed = [...(cfg.imageTypes ?? []), ...(cfg.videoTypes ?? [])];
  const isVid = (cfg.videoTypes ?? []).includes(file.type) || file.type.startsWith('video/');
  const max = isVid ? cfg.maxVideoBytes : cfg.maxImageBytes;
  return { ...(allowed.length ? { allowedFileTypes: allowed } : {}), ...(max > 0 ? { maxFileSize: max } : {}) };
}

/** Uppy/Transloadit error → one sentence for a toast. */
function uppyMessage(e: unknown, fallback: string): string {
  const raw = ((e as Error)?.message || '').trim();
  if (!raw) return fallback;
  // "Transloadit: Could not create Assembly: …" → keep the reason, lose the plugin prefix
  return raw.replace(/^Transloadit:\s*/i, '').trim() || fallback;
}

/**
 * Uploads ONE file into the signed Transforge assembly of `ticket` over tus, exactly like the
 * SCIO portal (Uppy v3 core + @uppy/transloadit pointed at Transforge). Headless: no Dashboard —
 * the caller renders its own progress from `onProgress`.
 *
 * Lifecycle (SCIO rules):
 * - one Uppy instance per upload, one assembly per file (`maxNumberOfFiles: 1`);
 * - `waitForEncoding: false` — Transforge's /ws never signals completion; the api learns the
 *   result (notify / poll) and the Processing chip polls `fileUrls`;
 * - `limit: 20` — the plugin shares one queue between assembly status polling and tus requests;
 *   on Transforge polling never stops, so a low limit starves the upload;
 * - ALWAYS `close({ reason: 'unmount' })` — the default reason ('user') cancels the assembly
 *   server-side, killing processing that is still running.
 */
export class TransforgeUploader {
  private uppy: Uppy | null = null;
  private assembly: { id: string; url: string | null } | null = null;

  constructor(
    private readonly ticket: UploadTicket,
    private readonly restrictions: UploadRestrictions = {},
  ) {}

  async upload(file: File, onProgress?: (pct: number) => void): Promise<{ assemblyId: string; assemblyUrl: string | null }> {
    if (file.size === 0) throw new UploadError(`${file.name || 'file'} is empty (0 bytes)`);
    const tf = this.ticket.transforge;
    let fields: Record<string, string | number> = {};
    if (typeof tf.fields === 'string') {
      try {
        fields = JSON.parse(tf.fields) as Record<string, string | number>;
      } catch {
        fields = {};
      }
    } else if (tf.fields) fields = tf.fields;

    const [{ default: UppyCore }, { default: Transloadit }] = await Promise.all([
      import('@uppy/core'),
      import('@uppy/transloadit'),
    ]).catch(() => {
      throw new UploadError('Upload could not start — reload the page and try again');
    });
    const uppy = new UppyCore({
      autoProceed: false,
      allowMultipleUploads: false,
      restrictions: { maxNumberOfFiles: 1, ...this.restrictions },
    });
    this.uppy = uppy;
    uppy.use(Transloadit, {
      service: tf.service,
      // params stays the server's JSON string: the signature is over those exact bytes
      assemblyOptions: () => ({ params: tf.params, signature: tf.signature, fields }) as unknown as AssemblyOptions,
      waitForEncoding: false,
      waitForMetadata: false,
      limit: 20,
    });

    let lastError: string | null = null;
    uppy.on('upload-progress', (_f, p) => {
      if (onProgress && p.bytesTotal) onProgress(Math.min(100, Math.round((p.bytesUploaded / p.bytesTotal) * 100)));
    });
    uppy.on('transloadit:assembly-created', (a) => {
      this.assembly = { id: a.assembly_id, url: a.assembly_ssl_url || a.assembly_url || null };
    });
    uppy.on('upload-error', (_f, err) => {
      lastError = uppyMessage(err, 'Upload failed');
    });
    uppy.on('error', (err) => {
      lastError ??= uppyMessage(err, 'Upload failed');
    });

    try {
      try {
        uppy.addFile({ name: file.name || 'file', type: file.type, data: file, source: 'Local', isRemote: false });
      } catch (e) {
        // restriction: type / size / count — Uppy's own sentence
        throw new UploadError(uppyMessage(e, 'This file cannot be uploaded'));
      }
      let result: Awaited<ReturnType<Uppy['upload']>> | undefined;
      try {
        result = await uppy.upload();
      } catch (e) {
        throw new UploadError(lastError ?? uppyMessage(e, 'Upload failed'));
      }
      if (!result || result.failed.length > 0 || result.successful.length === 0) {
        throw new UploadError(lastError ?? 'Upload failed');
      }
      const done = result.successful[0] as { transloadit?: { assembly?: string } };
      const assemblyId = this.assembly?.id || done.transloadit?.assembly || '';
      if (!assemblyId) throw new UploadError('Upload finished but no assembly was created');
      onProgress?.(100);
      return { assemblyId, assemblyUrl: this.assembly?.url ?? null };
    } finally {
      this.close();
    }
  }

  /** Never the default reason: 'user' cancels the assembly server-side. */
  close() {
    const u = this.uppy;
    this.uppy = null;
    if (u) {
      try {
        u.close({ reason: 'unmount' });
      } catch {
        // already closed
      }
    }
  }
}

type CreateUploadInputVars = {
  kind: 'attachment' | 'avatar';
  taskId?: number;
  filename: string;
  contentType: string;
  size: number;
  crop?: { x: number; y: number; size: number };
};

async function createTicket(client: ApolloClient, input: CreateUploadInputVars): Promise<UploadTicket> {
  try {
    const r = await client.mutate<{ createUpload: UploadTicket }>({ mutation: CREATE_UPLOAD, variables: { input } });
    if (!r.data?.createUpload?.transforge) throw new UploadError('Upload refused');
    return r.data.createUpload;
  } catch (e) {
    throw new UploadError(serverMessage(e, 'Upload refused'));
  }
}

async function complete<T>(
  client: ApolloClient,
  mutation: typeof COMPLETE_UPLOAD,
  fileId: string,
  a: { assemblyId: string; assemblyUrl: string | null },
): Promise<T> {
  try {
    const r = await client.mutate<{ completeUpload: T }>({
      mutation,
      variables: { fileId, assemblyId: a.assemblyId, assemblyUrl: a.assemblyUrl },
    });
    if (!r.data) throw new UploadError('Upload could not be completed');
    return r.data.completeUpload;
  } catch (e) {
    throw new UploadError(serverMessage(e, 'Upload could not be completed'));
  }
}

/**
 * createUpload → tus into the Transforge assembly (TransforgeUploader) → completeUpload with the
 * assembly id. Throws UploadError carrying a message fit for a toast.
 */
export async function uploadFile(
  client: ApolloClient,
  o: { taskId: number; file: File; cfg?: FilesConfig | null; onProgress?: (pct: number) => void },
): Promise<{ file: StoredFile; ref: string }> {
  if (o.file.size === 0) throw new UploadError('File is empty (0 bytes)');
  const contentType = o.file.type || 'application/octet-stream';
  const ticket = await createTicket(client, {
    kind: 'attachment',
    taskId: o.taskId,
    filename: o.file.name || 'file',
    contentType,
    size: o.file.size,
  });
  const a = await new TransforgeUploader(ticket, restrictionsFor(o.cfg, o.file)).upload(o.file, o.onProgress);
  const file = await complete<StoredFile>(client, COMPLETE_UPLOAD, ticket.fileId, a);
  remember(file);
  return { file, ref: ticket.ref || fileRef(ticket.fileId, o.file.name) };
}

const COMPLETE_AVATAR_UPLOAD = gql`
  mutation CompleteAvatarUpload($fileId: ID!, $assemblyId: String!, $assemblyUrl: String) {
    completeUpload(fileId: $fileId, assemblyId: $assemblyId, assemblyUrl: $assemblyUrl) {
      id
      status
    }
  }
`;

export const REMOVE_AVATAR = gql`
  mutation RemoveAvatar {
    removeAvatar
  }
`;

/**
 * Profile photo: createUpload(kind avatar, with the square crop in ORIGINAL pixels — it is baked
 * into the signed assembly) → tus the original bytes → completeUpload. Transforge crops + resizes;
 * `me.avatarUrl` flips once it is ready. Throws UploadError carrying a message fit for a toast.
 */
export async function uploadAvatar(
  client: ApolloClient,
  o: {
    file: File;
    crop: { x: number; y: number; size: number };
    restrictions?: UploadRestrictions;
    onProgress?: (pct: number) => void;
  },
): Promise<{ id: string; status: string }> {
  if (o.file.size === 0) throw new UploadError('File is empty (0 bytes)');
  const ticket = await createTicket(client, {
    kind: 'avatar',
    filename: o.file.name || 'avatar',
    contentType: o.file.type || 'application/octet-stream',
    size: o.file.size,
    crop: o.crop,
  });
  const a = await new TransforgeUploader(ticket, o.restrictions).upload(o.file, o.onProgress);
  return complete<{ id: string; status: string }>(client, COMPLETE_AVATAR_UPLOAD, ticket.fileId, a);
}

/* ── upload progress (editor placeholders read it by upload id) ───────────────── */

const progress = new Map<string, number>();
const progressSubs = new Set<() => void>();
export function setUploadProgress(uploadId: string, pct: number | null) {
  if (pct === null) progress.delete(uploadId);
  else progress.set(uploadId, pct);
  progressSubs.forEach((f) => f());
}
export function useUploadProgress(uploadId: string): number {
  return useSyncExternalStore(
    (f) => {
      progressSubs.add(f);
      return () => progressSubs.delete(f);
    },
    () => progress.get(uploadId) ?? 0,
  );
}

/* ── signed-link cache ──────────────────────────────────────────────────────── */

/** `unavailable`: the api omitted the id (unknown, deleted or not visible to this viewer). */
export type FileEntry = { file: StoredFile; fetchedAt: number } | { unavailable: true; fetchedAt: number };

const cache = new Map<string, FileEntry>();
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());

function remember(f: StoredFile, at = Date.now()) {
  cache.set(f.id.toLowerCase(), { file: f, fetchedAt: at });
}

/** Seed the cache from a payload that already carries the files (TaskDetail.files). */
export function seedFiles(files: StoredFile[] | null | undefined) {
  if (!files?.length) return;
  const at = Date.now();
  let changed = false;
  for (const f of files) {
    const cur = cache.get(f.id.toLowerCase());
    // keep a fresher entry; a later payload with a link always wins over an older one
    if (!cur || 'unavailable' in cur || cur.fetchedAt <= at) {
      remember(f, at);
      changed = true;
    }
  }
  if (changed) notify();
}

const POLL_MS = 3000;

/** When an entry needs refetching (ms from now); Infinity = never. */
function dueIn(e: FileEntry | undefined, now = Date.now()): number {
  if (!e) return 0;
  if ('unavailable' in e) return Infinity;
  const f = e.file;
  if (f.status === 'processing' || f.status === 'uploading') return Math.max(0, e.fetchedAt + POLL_MS - now);
  if (f.status !== 'ready' || !f.expiresAt) return Infinity;
  const exp = Date.parse(f.expiresAt);
  if (!Number.isFinite(exp)) return Infinity;
  const life = Math.max(0, exp - e.fetchedAt);
  return Math.max(0, e.fetchedAt + life * 0.8 - now);
}

// One batched fileUrls call per tick for every id that is due.
const pending = new Set<string>();
const inFlight = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
function request(client: ApolloClient, id: string) {
  if (inFlight.has(id)) return;
  pending.add(id);
  if (flushTimer) return;
  flushTimer = setTimeout(async () => {
    flushTimer = null;
    const ids = [...pending];
    pending.clear();
    ids.forEach((i) => inFlight.add(i));
    try {
      const r = await client.query<{ fileUrls: StoredFile[] }>({ query: FILE_URLS, variables: { ids }, fetchPolicy: 'no-cache' });
      const at = Date.now();
      const got = new Set<string>();
      for (const f of r.data?.fileUrls ?? []) {
        remember(f, at);
        got.add(f.id.toLowerCase());
      }
      for (const i of ids) if (!got.has(i)) cache.set(i, { unavailable: true, fetchedAt: at });
    } catch {
      // transient: leave entries as they are; the next due check retries
      const at = Date.now();
      for (const i of ids) if (!cache.has(i)) cache.set(i, { unavailable: true, fetchedAt: at });
    } finally {
      ids.forEach((i) => inFlight.delete(i));
      notify();
    }
  }, 0);
}

/** The file behind `infras-file:<id>`, kept fresh (poll while processing, refresh before expiry). */
export function useStoredFile(id: string | null): FileEntry | undefined {
  const client = useApolloClient();
  const entry = useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => (id ? cache.get(id) : undefined),
  );
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!id) return;
    const wait = dueIn(entry);
    if (wait === Infinity) return;
    timer.current = setTimeout(() => request(client, id), wait);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [id, entry, client]);
  return entry;
}

/* ── attach context: which task an editor attaches to ────────────────────────── */

/**
 * Provided by pages whose editors may attach files. `taskId: null` = attaching is not
 * possible yet (the new-task form: the task does not exist) — Attach shows disabled with `hint`.
 * No provider = no Attach button at all.
 */
export interface AttachTarget {
  taskId: number | null;
  hint?: string;
}
const AttachContext = createContext<AttachTarget | null>(null);
export function AttachProvider({ value, children }: { value: AttachTarget; children: ReactNode }) {
  return createElement(AttachContext.Provider, { value }, children);
}
export const useAttachTarget = () => useContext(AttachContext);

export const isVideo = (f: StoredFile) => f.contentType.startsWith('video/');
